import { addToast } from "@caprikorn/dynamo-ui";
import {
  type FetchiosError,
  type FetchiosResponse,
  downloadData,
} from "@caprikorn/dynamo-utils";
import { useMutation } from "@tanstack/react-query";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { apiEcmsHttp } from "@services/http";
import { QUERY_KEYS } from "@services/keys";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import type {
  DocumentDownloadInfo,
  PostDocumentsGroupDownloadParams,
  UseDocumentDownloadMutationParams,
} from "./apiEcms.types";

const postDocumentsGroupDownload = ({
  fileName,
  s3VersionID,
  fileExtension,
}: PostDocumentsGroupDownloadParams) =>
  apiEcmsHttp.post<object, FetchiosResponse<Blob>>(
    "/documents/group/download",
    {
      refDocument: fileName,
      s3VersionId: s3VersionID,
      extension: fileExtension,
    },
    {
      responseType: "blob",
    },
  );

export const usePostDocumentsGroupDownload = ({
  supportEmail,
}: UseDocumentDownloadMutationParams) => {
  const { currentGroup } = useGroupReferences();
  const { t } = useCustomTranslation("main");

  return useMutation<
    FetchiosResponse<Blob>,
    FetchiosError,
    DocumentDownloadInfo
  >({
    mutationFn: postDocumentsGroupDownload,
    mutationKey: [QUERY_KEYS.GROUP_DOCUMENTS, currentGroup],
    retry: false,
    onSuccess: (response, args) => {
      if (!response.data) {
        addToast({
          variant: "warning",
          title: t("documents.toast.emptyError.title"),
          description: t("documents.toast.emptyError.description"),
        });

        return;
      }

      downloadData(args.fileName, response.data);
    },

    onError: (error) => {
      if (error.status === 413) {
        addToast({
          variant: "error",
          title: t("documents.toast.largeFileError.title"),
          description: t("documents.toast.largeFileError.description", {
            supportEmail,
          }),
        });

        return;
      }

      addToast({
        variant: "error",
        title: t("documents.toast.extractDownloadError.title"),
        description: t("documents.toast.extractDownloadError.description", {
          supportEmail,
        }),
      });
    },
  });
};
